import logger from "lib/utils/logger"

export function convertStringToRatio(ratioString: string): number | undefined {
  if (typeof ratioString !== "string") {
    logger.error("Invalid ratio format")
    return undefined
  }
  const parts = ratioString.split(":") || ["2", "3"]

  // Ensure we have exactly two parts
  if (parts.length !== 2) {
    logger.error("Invalid ratio format")
    return undefined
  }

  const numerator = parseInt(parts[0] ?? "")
  const denominator = parseInt(parts[1] ?? "")

  // Check if parsing was successful and denominator is not zero
  if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
    return numerator / denominator
  } else {
    logger.error("Invalid ratio format")
    return undefined
  }
}

export function getIdFromGid(gid: string | number | null | undefined): string {
  if (!gid) return ""

  // Coerce before splitting: search-response products (and forked blocks'
  // action payloads) can carry numeric ids, and `(123).split` throws.
  // Anything that isn't a string or number is not an id — coercing an object
  // would yield "[object Object]" and attribute an interaction to a garbage
  // product rather than skipping it. Callers are untyped block payloads, so
  // the type signature alone doesn't rule this out.
  if (typeof gid !== "string" && typeof gid !== "number") return ""

  const arr = String(gid).split("/")
  const id = arr[arr.length - 1] || ""
  return id
}

/**
 * The Shopify cart token from a Storefront cart id.
 *
 * Analytics vendors (Fast Simon among them) join their sessions to Shopify
 * orders on this token, so it has to match what Shopify records on the order —
 * the id's last path segment, WITHOUT the `?key=` query a Storefront cart id
 * carries. Accepts an already-bare token unchanged.
 */
export function getCartTokenFromCartId(
  cartId: string | null | undefined
): string {
  if (!cartId || typeof cartId !== "string") return ""
  // Split on an encoded `%3F` as well as a literal `?`. The query holds the
  // cart's secret `key`, and ids reaching us via a URL round-trip can arrive
  // percent-encoded — matching only `?` would ship that key to the vendor.
  const withoutQuery = cartId.split(/\?|%3F/i)[0]
  return withoutQuery.split("/").pop() || ""
}

export function gidFromId(id: string) {
  if (id.startsWith("gid://shopify/Product/")) return id
  return `gid://shopify/Product/${id}`
}

export function gidFromVariantId(id: string) {
  if (id.startsWith("gid://shopify/ProductVariant/")) return id
  return `gid://shopify/ProductVariant/${id}`
}

export function transformAuthor(author: string) {
  const [first, last = ""] = author?.split(" ") || []
  return first ? `${first}${last ? ` ${last.charAt(0)}.` : ""}` : ""
}
